import { createMachine, assign } from 'xstate';
import logger from '../../logger';

// --- Tailwind Button Classes ---
const baseButtonClasses = '';

// Define default button structures with Tailwind classes
const defaultPrimaryButton = {
  title: 'Save',
  // Combine base classes with primary-specific styles
  className: `${baseButtonClasses}`,
  show: true, // Default show state
  callback: () => {
    logger.warn('Default Primary Button Callback Triggered'); // Use warn for defaults
  },
};

const defaultSecondaryButton = {
  title: 'Cancel',
  // Combine base classes with secondary-specific styles
  className: `${baseButtonClasses}`,
  show: true, // Default show state
  callback: () => {
    logger.warn('Default Secondary Button Callback Triggered'); // Use warn for defaults
  },
};
// --- End Tailwind Button Classes ---

export const modalMachine = createMachine({
  predictableActionArguments: true,
  id: 'modal',

  // Initial state
  initial: 'inactive',

  // Local context for entire machine
  context: {
    showCloseModal: false,
    sectionClassName: '',
    title: null,
    content: null,
    // Initialize context with defaults using Tailwind classes
    primaryButton: { ...defaultPrimaryButton },
    secondaryButton: { ...defaultSecondaryButton },
  },

  // State definitions
  states: {
    inactive: {
      // *** IMPORTANT: Corrected 'on: { entry: [...] }' to just 'entry: [...]' ***
      entry: [
        assign((context, event) => {
          logger.info('modal inactive entry');
          // Reset context on entering inactive state for cleaner restarts
          return {
            showCloseModal: false,
            sectionClassName: '',
            title: null,
            content: null,
            primaryButton: { ...defaultPrimaryButton, show: false }, // Ensure hidden initially
            secondaryButton: { ...defaultSecondaryButton, show: false }, // Ensure hidden initially
          };
        }),
      ],
      // *** Moved transitions inside 'on' block ***
      on: {
        updateModal: {
          target: 'active',
          actions: assign((context, event) => {
            logger.info('updateModal (from inactive) event.data:', event.data);
            const {
              title,
              content,
              primaryButton, // Incoming button data (might be partial or undefined)
              secondaryButton, // Incoming button data (might be partial or undefined)
              sectionClassName,
              showCloseModal,
            } = event.data || {}; // Use default empty object if event.data is missing

            // Merge incoming button data with defaults. If no incoming data, use default.
            const finalPrimaryButton =
              primaryButton !== undefined
                ? { ...defaultPrimaryButton, ...primaryButton }
                : { ...defaultPrimaryButton }; // Fallback to default if not provided

            const finalSecondaryButton =
              secondaryButton !== undefined
                ? { ...defaultSecondaryButton, ...secondaryButton }
                : { ...defaultSecondaryButton }; // Fallback to default if not provided

            // Ensure buttons are shown when activating via updateModal unless explicitly told not to
            if (primaryButton?.show !== false) finalPrimaryButton.show = true;
            if (secondaryButton?.show !== false)
              finalSecondaryButton.show = true;

            return {
              // Only update if provided in event.data, otherwise keep existing context value (which are defaults here)
              title: title !== undefined ? title : null,
              content: content !== undefined ? content : null,
              sectionClassName:
                sectionClassName !== undefined ? sectionClassName : '',
              showCloseModal:
                showCloseModal !== undefined ? showCloseModal : false,
              primaryButton: finalPrimaryButton,
              secondaryButton: finalSecondaryButton,
            };
          }),
        },
        closeModal: {
          target: 'inactive', // Already inactive, but good for explicit close actions
        },
        toggle: {
          target: 'active',
          // *** Corrected 'entry' to 'actions' for transition actions ***
          actions: assign((context, event) => {
            logger.info('inactive - toggle to active');
            // If toggling to active without specific data, ensure defaults are shown
            return {
              primaryButton: { ...context.primaryButton, show: true },
              secondaryButton: { ...context.secondaryButton, show: true },
            };
          }),
        },
      },
    },
    active: {
      entry: [
        (context, event) => {
          logger.debug({ context, event }, 'modal active entry data');
        },
      ],
      on: {
        toggle: {
          target: 'inactive',
          // *** Corrected 'entry' to 'actions' for transition actions ***
          actions: assign((context, event) => {
            logger.info('active - toggle to inactive');
            // Resetting happens in 'inactive' state's entry action
          }),
        },
        closeModal: {
          // Explicit close action from active state
          target: 'inactive',
          // Resetting happens in 'inactive' state's entry action
        },
        updateModal: {
          target: 'active', // Stay active
          actions: assign((context, event) => {
            const {
              title,
              content,
              primaryButton, // Incoming button data (might be partial or undefined)
              secondaryButton, // Incoming button data (might be partial or undefined)
              sectionClassName,
              showCloseModal,
            } = event.data || {}; // Use default empty object if event.data is missing

            // logger.info('updateModal (from inactive) data received:', {
            //   title,
            //   content,
            //   // Log only safe button properties, avoid logging callbacks directly
            //   primaryButton: primaryButton
            //     ? {
            //         title: primaryButton.title,
            //         className: primaryButton.className,
            //         show: primaryButton.show,
            //       }
            //     : undefined,
            //   secondaryButton: secondaryButton
            //     ? {
            //         title: secondaryButton.title,
            //         className: secondaryButton.className,
            //         show: secondaryButton.show,
            //       }
            //     : undefined,
            //   sectionClassName,
            //   showCloseModal,
            // });

            // Merge incoming button data with defaults. If no incoming data, keep existing.
            // *** Use context's current button as base for merge when active ***
            const finalPrimaryButton =
              primaryButton !== undefined
                ? { ...context.primaryButton, ...primaryButton } // Merge incoming over current
                : context.primaryButton; // Keep existing if not provided

            const finalSecondaryButton =
              secondaryButton !== undefined
                ? { ...context.secondaryButton, ...secondaryButton } // Merge incoming over current
                : context.secondaryButton; // Keep existing if not provided

            return {
              // Only update if provided in event.data, otherwise keep existing context value
              title: title !== undefined ? title : context.title,
              content: content !== undefined ? content : context.content,
              sectionClassName:
                sectionClassName !== undefined
                  ? sectionClassName
                  : context.sectionClassName,
              showCloseModal:
                showCloseModal !== undefined
                  ? showCloseModal
                  : context.showCloseModal,
              primaryButton: finalPrimaryButton,
              secondaryButton: finalSecondaryButton,
            };
          }),
        },
      },
    },
  },
});
